import React, { useContext, useEffect, useState } from "react";
import { Moon, Sun } from "lucide-react";

import ThemeContext from "../../../contexts/theme";
import { translate } from "../../../utils/Common";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuCheckItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LANGUAGES = [
  { code: "en", labelKey: "translate.en", flag: "/img/en_flag.png" },
  { code: "es", labelKey: "translate.es", flag: "/img/es_flag.png" },
  { code: "pt", labelKey: "translate.pt", flag: "/img/br_flag.png" },
];

/**
 * Language and theme controls for the auth screens, as a single segmented pill.
 *
 * The shared LanguageSelector opens a full-screen modal with language cards to
 * change one setting, which is a lot of ceremony for picking a language. Here it
 * is a dropdown, sitting flush against the theme toggle so the two read as one
 * control instead of two floating buttons.
 */
const AuthControls = ({ className }) => {
  const { theme, toggleTheme } = useContext(ThemeContext) || { theme: "light" };
  const [language, setLanguage] = useState("en");

  useEffect(() => {
    const stored = localStorage.getItem("language");
    if (stored) {
      setLanguage(stored);
      return;
    }
    const browserLang = window.navigator.language?.split("-")[0];
    const fallback = LANGUAGES.some((l) => l.code === browserLang) ? browserLang : "en";
    setLanguage(fallback);
    localStorage.setItem("language", fallback);
  }, []);

  const selectLanguage = (code) => {
    if (code === language) return;
    localStorage.setItem("language", code);
    // `translate` reads the language at module scope, so the strings already
    // rendered only change on reload. Same behaviour as the previous selector.
    window.location.reload();
  };

  const current = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];
  const isDark = theme === "dark";

  return (
    <div
      className={cn(
        "inline-flex items-center gap-0.5 rounded-full border border-border/60 bg-card/70 p-1 backdrop-blur-md",
        className
      )}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={translate("bottomNav.language")}
            className="flex items-center gap-2 rounded-full border-0 bg-transparent px-2.5 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <img
              src={current.flag}
              alt=""
              aria-hidden="true"
              className="size-4 rounded-full object-cover"
            />
            {current.code.toUpperCase()}
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end">
          {LANGUAGES.map((lang) => (
            <DropdownMenuCheckItem
              key={lang.code}
              checked={lang.code === language}
              onSelect={() => selectLanguage(lang.code)}
            >
              <img
                src={lang.flag}
                alt=""
                aria-hidden="true"
                className="size-4 rounded-full object-cover"
              />
              {translate(lang.labelKey)}
            </DropdownMenuCheckItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <span aria-hidden="true" className="h-4 w-px bg-border" />

      <button
        type="button"
        onClick={toggleTheme}
        role="switch"
        aria-checked={isDark}
        aria-label={isDark ? translate("theme.toLight") : translate("theme.toDark")}
        title={isDark ? translate("theme.toLight") : translate("theme.toDark")}
        className="relative flex size-8 items-center justify-center overflow-hidden rounded-full border-0 bg-transparent text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {/* Both icons stay mounted and cross-fade, so the swap reads as one
            control changing state rather than two icons replacing each other. */}
        <Sun
          className={cn(
            "absolute size-4 transition-all duration-300",
            isDark ? "-rotate-90 scale-0 opacity-0" : "rotate-0 scale-100 opacity-100"
          )}
          aria-hidden="true"
        />
        <Moon
          className={cn(
            "absolute size-4 transition-all duration-300",
            isDark ? "rotate-0 scale-100 opacity-100" : "rotate-90 scale-0 opacity-0"
          )}
          aria-hidden="true"
        />
      </button>
    </div>
  );
};

export default AuthControls;
